// MemberList — roster with roles, presence, and admin moderation
// actions.
//
// Renders three sections (Admins / Moderators / Members) derived from
// the power-level snapshot in `m.room.power_levels`. Each row carries
// an overflow button; if the caller has admin power the menu offers
// Kick and Ban, both routed through a confirmation dialog. Without
// admin power the menu is hidden — which matches the server-side
// truth that the homeserver would reject the action anyway.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { MoreHorizontal } from "lucide-react";

import { Dialog } from "~/components/Dialog";
import { DialogFooter, DialogHeader, ErrorLine } from "~/components/CreateRoomDialog";
import { DeviceVerificationLauncher } from "~/components/DeviceVerificationLauncher";
import { DangerButton, GhostButton } from "~/components/MessageButtons";
import { PresenceIndicator } from "~/components/PresenceIndicator";
import { MemberKindBadge } from "~/components/MemberKindBadge";
import { UserProfileCard } from "~/components/UserProfileCard";
import { UserAvatar } from "~/components/UserSearch";
import { useMatrix } from "~/context/MatrixContext";
import { usePresence } from "~/hooks/usePresence";
import { POWER_LEVEL_ADMIN, POWER_LEVEL_MODERATOR, useRoomSettings } from "~/hooks/useRoomSettings";
import { getUserDevices, subscribeRooms, type RoomMember } from "~/lib/matrix";
import { ICON_STROKE } from "~/lib/icon";

type RoleKey = "admin" | "moderator" | "member";

function roleOf(member: RoomMember): RoleKey {
  if (member.powerLevel >= POWER_LEVEL_ADMIN) return "admin";
  if (member.powerLevel >= POWER_LEVEL_MODERATOR) return "moderator";
  return "member";
}

export interface MemberListProps {
  roomId: string;
}

export function MemberList({ roomId }: MemberListProps) {
  const settings = useRoomSettings(roomId);
  const { userId: myUserId, client } = useMatrix();
  const [members, setMembers] = useState<RoomMember[]>(() => settings.getMembers());
  const [pendingAction, setPendingAction] = useState<{
    kind: "kick" | "ban";
    member: RoomMember;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Read `getMembers` through a ref so the subscription effect can key
  // on identifiers alone. Depending on `settings` is the infinite-loop
  // vector — a fresh array from `getMembers` would re-run the effect.
  const getMembersRef = useRef(settings.getMembers);
  getMembersRef.current = settings.getMembers;

  useEffect(() => {
    if (!client) return;
    // Re-deriving the roster on each rooms emission is what makes
    // membership live rather than a mount-only snapshot.
    return subscribeRooms(client, () => {
      setMembers(getMembersRef.current());
    });
  }, [client, roomId]);

  const canManage = settings.canManageMembers();

  const grouped = useMemo(() => {
    const admins: RoomMember[] = [];
    const moderators: RoomMember[] = [];
    const rest: RoomMember[] = [];
    for (const m of members) {
      const role = roleOf(m);
      if (role === "admin") admins.push(m);
      else if (role === "moderator") moderators.push(m);
      else rest.push(m);
    }
    const byName = (a: RoomMember, b: RoomMember) => a.displayName.localeCompare(b.displayName);
    admins.sort(byName);
    moderators.sort(byName);
    rest.sort(byName);
    return { admins, moderators, rest };
  }, [members]);

  // My own power level — used to gate the overflow menu against the
  // target member's level so we never surface "kick" on a peer or a
  // higher-ranked user. Matrix enforces this server-side; hiding the
  // affordance keeps the UI honest.
  const myPowerLevel = useMemo(
    () => members.find((m) => m.userId === myUserId)?.powerLevel ?? 0,
    [members, myUserId],
  );

  const handleConfirm = useCallback(async () => {
    if (!pendingAction) return;
    setError(null);
    try {
      if (pendingAction.kind === "kick") {
        await settings.kick(pendingAction.member.userId);
      } else {
        await settings.ban(pendingAction.member.userId);
      }
      setPendingAction(null);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Couldn't ${pendingAction.kind} ${pendingAction.member.displayName}.`,
      );
    }
  }, [pendingAction, settings]);

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <div style={{ overflowY: "auto", overscrollBehavior: "contain", flex: 1 }}>
        <Group
          label="Admins"
          members={grouped.admins}
          roomId={roomId}
          canManage={canManage}
          myUserId={myUserId}
          myPowerLevel={myPowerLevel}
          onKick={(m) => setPendingAction({ kind: "kick", member: m })}
          onBan={(m) => setPendingAction({ kind: "ban", member: m })}
        />
        <Group
          label="Moderators"
          members={grouped.moderators}
          roomId={roomId}
          canManage={canManage}
          myUserId={myUserId}
          myPowerLevel={myPowerLevel}
          onKick={(m) => setPendingAction({ kind: "kick", member: m })}
          onBan={(m) => setPendingAction({ kind: "ban", member: m })}
        />
        <Group
          label="Members"
          members={grouped.rest}
          roomId={roomId}
          canManage={canManage}
          myUserId={myUserId}
          myPowerLevel={myPowerLevel}
          onKick={(m) => setPendingAction({ kind: "kick", member: m })}
          onBan={(m) => setPendingAction({ kind: "ban", member: m })}
        />
      </div>
      {pendingAction && (
        <ConfirmModerationDialog
          action={pendingAction.kind}
          member={pendingAction.member}
          onCancel={() => {
            setPendingAction(null);
            setError(null);
          }}
          onConfirm={handleConfirm}
          error={error}
        />
      )}
    </div>
  );
}

function Group({
  label,
  members,
  roomId,
  canManage,
  myUserId,
  myPowerLevel,
  onKick,
  onBan,
}: {
  label: string;
  members: RoomMember[];
  roomId: string;
  canManage: boolean;
  myUserId: string | null;
  myPowerLevel: number;
  onKick: (m: RoomMember) => void;
  onBan: (m: RoomMember) => void;
}) {
  if (members.length === 0) return null;
  return (
    <section aria-label={label}>
      <h3
        style={{
          margin: 0,
          height: "var(--chat-sidebar-section-header-height)",
          padding: "0 var(--chat-space-4)",
          display: "flex",
          alignItems: "center",
          color: "var(--chat-fg-muted)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "10px",
          lineHeight: "var(--chat-type-body-xs-line)",
          textTransform: "uppercase",
          letterSpacing: "0.12em",
        }}
      >
        {label}
      </h3>
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
        }}
      >
        {members.map((m) => (
          <li key={m.userId}>
            <MemberRow
              member={m}
              // Hide overflow menu on self and on peers at or above
              // our power level. Matrix rejects kick/ban against a
              // user with >= our PL server-side; surfacing the menu
              // would be a guaranteed toast failure.
              canManage={canManage && m.userId !== myUserId && m.powerLevel < myPowerLevel}
              roomId={roomId}
              myUserId={myUserId}
              onKick={() => onKick(m)}
              onBan={() => onBan(m)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function MemberAvatarWithPresence({
  userId,
  displayName,
}: {
  userId: string;
  displayName: string;
}) {
  const { client } = useMatrix();
  const { state } = usePresence(client, userId);
  return (
    <span style={{ position: "relative", flexShrink: 0 }}>
      <UserAvatar name={displayName} size={32} />
      <PresenceIndicator state={state} surface="panel" />
    </span>
  );
}

function MemberRow({
  member,
  canManage,
  roomId,
  myUserId,
  onKick,
  onBan,
}: {
  member: RoomMember;
  canManage: boolean;
  roomId: string;
  myUserId: string | null;
  onKick: () => void;
  onBan: () => void;
}) {
  const { client } = useMatrix();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);
  const role = roleOf(member);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        height: "var(--chat-member-row-height)",
        padding: "0 var(--chat-space-4)",
        position: "relative",
      }}
    >
      <button
        type="button"
        aria-label={`Open profile for ${member.displayName}`}
        onClick={(event) => setProfileAnchor(event.currentTarget)}
        className="chat-button"
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-3)",
          border: "none",
          background: "transparent",
          padding: 0,
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <MemberAvatarWithPresence userId={member.userId} displayName={member.displayName} />
        <span style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column" }}>
          <span
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: "var(--chat-space-1)",
              minWidth: 0,
            }}
          >
            <span
              style={{
                color: "var(--chat-fg-primary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-md-size)",
                lineHeight: "var(--chat-type-body-md-line)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {member.displayName}
            </span>
            <MemberKindBadge userId={member.userId} />
          </span>
          <span
            style={{
              color: "var(--chat-fg-muted)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {member.userId}
          </span>
        </span>
        {role !== "member" && <RoleChip role={role} />}
      </button>
      {member.userId !== myUserId ? (
        <DeviceVerificationLauncher
          client={client}
          userId={member.userId}
          loadDevices={() => {
            if (!client) return Promise.resolve([]);
            return getUserDevices(client, member.userId);
          }}
        />
      ) : null}
      {canManage && (
        <div style={{ position: "relative" }}>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            aria-label={`More actions for ${member.displayName}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="chat-button"
            style={{
              width: "var(--chat-dialog-close-size)",
              height: "var(--chat-dialog-close-size)",
              border: "none",
              background: "transparent",
              color: "var(--chat-fg-secondary)",
              borderRadius: "var(--chat-radius-sm)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <MoreHorizontal size={14} strokeWidth={ICON_STROKE} />
          </button>
          {menuOpen && (
            <ActionMenu
              onKick={() => {
                setMenuOpen(false);
                onKick();
              }}
              onBan={() => {
                setMenuOpen(false);
                onBan();
              }}
              onDismiss={() => setMenuOpen(false)}
            />
          )}
        </div>
      )}
      {profileAnchor ? (
        <UserProfileCard
          userId={member.userId}
          fallbackDisplayName={member.displayName}
          fallbackAvatarUrl={member.avatarUrl}
          anchor={profileAnchor}
          roomId={roomId}
          onClose={() => setProfileAnchor(null)}
        />
      ) : null}
    </div>
  );
}

function ActionMenu({
  onKick,
  onBan,
  onDismiss,
}: {
  onKick: () => void;
  onBan: () => void;
  onDismiss: () => void;
}) {
  useEffect(() => {
    function handle(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-member-action-menu]")) onDismiss();
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [onDismiss]);
  return (
    <div
      data-member-action-menu
      role="menu"
      onClick={(event) => event.stopPropagation()}
      style={{
        position: "absolute",
        right: 0,
        top: "100%",
        marginTop: "var(--chat-space-1)",
        minWidth: 120,
        padding: "var(--chat-space-1)",
        borderRadius: "var(--chat-radius-xl)",
        border: "1px solid var(--chat-surface-border)",
        background: "var(--chat-surface-overlay)",
        boxShadow: "var(--chat-shadow-popover)",
        zIndex: 2,
      }}
    >
      <MenuItem onClick={onKick}>Kick</MenuItem>
      <MenuItem onClick={onBan} danger>
        Ban
      </MenuItem>
    </div>
  );
}

function MenuItem({
  onClick,
  danger,
  children,
}: {
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="chat-button"
      style={{
        display: "block",
        width: "100%",
        padding: "var(--chat-space-1) var(--chat-space-3)",
        border: "none",
        borderRadius: "var(--chat-radius-sm)",
        background: "transparent",
        color: danger ? "var(--chat-state-danger)" : "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        textAlign: "left",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function RoleChip({ role }: { role: Exclude<RoleKey, "member"> }) {
  const label = role === "admin" ? "Admin" : "Moderator";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "10px",
        lineHeight: "var(--chat-type-body-xs-line)",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </span>
  );
}

function ConfirmModerationDialog({
  action,
  member,
  onCancel,
  onConfirm,
  error,
}: {
  action: "kick" | "ban";
  member: RoomMember;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
  error: string | null;
}) {
  const [busy, setBusy] = useState(false);
  const titleId = `${useId()}-confirm-title`;
  const title = action === "kick" ? `Kick ${member.displayName}?` : `Ban ${member.displayName}?`;
  const body =
    action === "kick"
      ? "They will be removed from the room. They can be re-invited later."
      : "They will be removed and can't rejoin until unbanned.";
  const primaryLabel = action === "kick" ? "Kick" : "Ban";
  const requestClose = () => {
    if (busy) return;
    onCancel();
  };
  return (
    <Dialog
      onClose={requestClose}
      labelledBy={titleId}
      width="var(--chat-dialog-confirm-width)"
      panelStyle={{ minHeight: "var(--chat-dialog-confirm-min-height)" }}
    >
      <DialogHeader title={title} titleId={titleId} onClose={requestClose} />
      <div
        style={{
          padding: "var(--chat-dialog-body-padding)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--chat-space-2)",
        }}
      >
        <p
          style={{
            margin: 0,
            color: "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
          }}
        >
          {body}
        </p>
        {error && <ErrorLine>{error}</ErrorLine>}
      </div>
      <DialogFooter>
        <GhostButton onClick={onCancel} disabled={busy}>
          Cancel
        </GhostButton>
        <DangerButton
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm();
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy}
        >
          {primaryLabel}
        </DangerButton>
      </DialogFooter>
    </Dialog>
  );
}
